import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Avatar,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Divider,
  Button
} from '@mui/material';
import { useSelector } from 'react-redux';
import CircleIcon from '@mui/icons-material/Circle';
import SquareIcon from '@mui/icons-material/Square';
import ChangeHistoryIcon from '@mui/icons-material/ChangeHistory';
import DiamondIcon from '@mui/icons-material/Diamond';
import RectangleIcon from '@mui/icons-material/Rectangle';
import StarIcon from '@mui/icons-material/Star';
import HexagonIcon from '@mui/icons-material/Hexagon';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import AddIcon from '@mui/icons-material/Add';
import PentagonIcon from '@mui/icons-material/Pentagon';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { getBaysCameraView, getBayDetailsCameraView } from '../../../api/index';
import ImageDetailsDialog from '../Table/BayDetails/DetailsDialog';
import { setHandlers } from '../Table/BayDetails/ImageBboxes';

const fixtureIconMap = {
  'Wall Bay': <CircleIcon sx={{ fontSize: 16, color: 'white' }} />,
  'Impulse Fixture': <SquareIcon sx={{ fontSize: 16, color: 'white' }} />,
  'A Rail': <ChangeHistoryIcon sx={{ fontSize: 16, color: 'white' }} />,
  'Long Table': <DiamondIcon sx={{ fontSize: 16, color: 'white' }} />,
  'H Gandola': <RectangleIcon sx={{ fontSize: 16, color: 'white' }} />,
  '4 Way': <HexagonIcon sx={{ fontSize: 16, color: 'white' }} />,
  'Nested Table': <StarIcon sx={{ fontSize: 16, color: 'white' }} />,
  'U Bar': <PanoramaFishEyeIcon sx={{ fontSize: 16, color: 'white' }} />,
  Gondola: <PentagonIcon sx={{ fontSize: 16, color: 'white' }} />,
  mannequine: <AddIcon sx={{ fontSize: 16, color: 'white' }} />
};

// Camera Card Styles
const cameraCardSx = {
  width: 220,
  m: 0,
  borderRadius: 3,
  boxShadow: 2,
  background: '#fff',
  transition: 'transform 0.15s, box-shadow 0.15s',
  // cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px) scale(1.03)',
    boxShadow: 6
  }
};

const CameraView = ({ store_oid, selectedBayId, onBackToStoreView }) => {
  const [loading, setLoading] = useState(true);
  const [selectedBay, setSelectedBay] = useState(null);
  const [bays, setBays] = useState([]);
  const [selectedBayImages, setSelectedBayImages] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [bayDetails, setBayDetails] = useState(null);

  // const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const selectedDate = useSelector((state) => state.customization.selectedDate)
    .toISOString()
    .slice(0, 10);

  useEffect(() => {
    const fetchBays = async () => {
      setLoading(true);
      try {
        const data = await getBaysCameraView({ store_oid });
        if (data && Array.isArray(data.data)) {
          // Remove duplicate bays by _id
          const uniqueBays = [];
          const seen = new Set();
          for (const bay of data.data) {
            if (!seen.has(bay._id)) {
              uniqueBays.push(bay);
              seen.add(bay._id);
            }
          }
          setBays(uniqueBays);

          // Auto-select bay based on selectedBayId or if only one bay exists
          let bayToSelect = null;
          if (selectedBayId) {
            bayToSelect = uniqueBays.find((bay) => bay._id === selectedBayId);
          } else if (uniqueBays.length === 1) {
            bayToSelect = uniqueBays[0];
          }

          if (bayToSelect) {
            setSelectedBay(bayToSelect);
            // Fetch images for the selected bay
            const imageData = await getBayDetailsCameraView({ bay_oid: bayToSelect._id, date: selectedDate });
            if (imageData && Array.isArray(imageData.data)) {
              setSelectedBayImages(imageData.data.filter(Boolean));
            } else {
              setSelectedBayImages([]);
            }
          } else {
            setSelectedBay(null);
            setSelectedBayImages([]);
          }
        } else {
          setBays([]);
          setSelectedBay(null);
          setSelectedBayImages([]);
        }
      } catch (error) {
        setBays([]);
        setSelectedBay(null);
        setSelectedBayImages([]);
      } finally {
        setLoading(false);
      }
    };
    fetchBays();
    // eslint-disable-next-line
  }, [store_oid, selectedDate, selectedBayId]);

  useEffect(() => {
    setHandlers(handleOpenDialog, setImageLoaded);
  }, []);

  const handleBaySelect = async (bay) => {
    setSelectedBay(bay);
    setSelectedBayImages([]);
    try {
      const data = await getBayDetailsCameraView({ bay_oid: bay._id, date: selectedDate });
      if (data && Array.isArray(data.data)) {
        setSelectedBayImages(data.data.filter(Boolean));
      } else {
        setSelectedBayImages([]);
      }
    } catch (error) {
      setSelectedBayImages([]);
    }
  };

  const getBayColor = (bay) => {
    if (!bay.hasCapture) return '#9e9e9e'; // gray when no capture
    const details = bay.captureDetails ?? [];
    const totalFullness = details.reduce((sum, item) => sum + (item.fullness ?? 0), 0);
    const avgFullness = details.length > 0 ? totalFullness / details.length : 0;
    if (avgFullness > 90) return '#4caf50'; // Green
    if (avgFullness >= 50) return '#ffb300'; // Amber
    return '#e53935'; // Red
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return { date: 'N/A', time: 'N/A' };
    try {
      const dateObj = new Date(timestamp);
      const date = dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
      const time = dateObj.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
      return { date, time };
    } catch (error) {
      return { date: 'N/A', time: 'N/A' };
    }
  };

  // Dialog handlers
  const handleOpenDialog = (imageObj, bay) => {
    const imageData = {
      image_url: imageObj.image_url,
      metadata: imageObj.metadata || [],
      userDetails: imageObj.userDetails || {},
      timestamps: imageObj.timestamps || imageObj.timestamp || imageObj.created_at || imageObj.createdAt || imageObj.date || null
    };

    const bayDetailsForDialog = {
      data: [
        {
          bay_id: bay.bay_id || bay.bay_name || bay._id,
          format: bay.format || bay.fixture_name || '',
          captureDetails: [imageData]
        }
      ]
    };
    setSelectedImage(imageData);
    setDialogOpen(true);
    setImageLoaded(false);
    setBayDetails(bayDetailsForDialog);
  };
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedImage(null);
    setImageLoaded(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Header text logic
  // const headerText = selectedBay
  //   ? `${selectedBay.bay_name || selectedBay.bay_id || 'Camera'} - Camera Views`
  //   : bays.length === 1 && bays[0]
  //   ? `${bays[0].bay_name || bays[0].bay_id || 'Camera'} - Camera Views`
  //   : 'Camera Views';

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', background: '#fafbfc' }}>
      {/* Back button when a specific bay is selected */}
      {selectedBayId && onBackToStoreView && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            onClick={onBackToStoreView}
            sx={{
              mb: 1,
              '&:hover': {
                backgroundColor: 'primary.light',
                color: 'primary.contrastText'
              }
            }}
          >
            ← Back to Store View
          </Button>
        </Box>
      )}
      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0, height: '100%' }}>
        {/* Left Side - Bay List */}
        <Grid item xs={12} md={4} sx={{ height: '100%', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', boxShadow: 1 }}>
            <CardContent sx={{ flex: 1, overflow: 'hidden', p: 0, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" sx={{ p: 2, borderBottom: 1, borderColor: 'divider', fontWeight: 600 }}>
                Bays ({bays.length})
              </Typography>
              <List sx={{ flex: 1, overflowY: 'auto', p: 0, maxHeight: 'calc(100vh - 220px)', minHeight: 0 }}>
                {bays.map((bay, index) => (
                  <React.Fragment key={bay._id}>
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={selectedBay?._id === bay._id}
                        onClick={() => handleBaySelect(bay)}
                        sx={{
                          '&.Mui-selected': {
                            background: 'linear-gradient(90deg, #e3f2fd 60%, #bbdefb 100%)',
                            boxShadow: 2,
                            borderLeft: '4px solid #1976d2',
                            '&:hover': {
                              background: 'linear-gradient(90deg, #e3f2fd 60%, #bbdefb 100%)'
                            }
                          },
                          borderRadius: 2,
                          my: 0.5,
                          mx: 1,
                          transition: 'background 0.2s, box-shadow 0.2s'
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              backgroundColor: getBayColor(bay),
                              width: 32,
                              height: 32
                            }}
                          >
                            {fixtureIconMap[bay.fixture_name] || <SquareIcon />}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={bay.bay_name || bay.bay_id}
                          secondary={
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                {bay.fixture_name} • {bay.type}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < bays.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
        {/* Right Side - Camera Images */}
        <Grid item xs={12} md={8} sx={{ height: '100%', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', boxShadow: 1, background: '#fff' }}>
            <CardContent sx={{ flex: 1, overflow: 'auto', p: 0, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" sx={{ p: 2, borderBottom: 1, borderColor: 'divider', fontWeight: 600, minHeight: 48 }}>
                {/* {headerText} */}
              </Typography>
              <Box sx={{ flex: 1, p: 2, minHeight: 0 }}>
                {selectedBay || (bays.length === 1 && bays[0]) ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 3,
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                      minHeight: 200,
                      background: '#f7f8fa',
                      borderRadius: 2,
                      p: 1
                    }}
                  >
                    {selectedBayImages.length > 0 ? (
                      selectedBayImages.map((image, index) => (
                        <Card key={index} variant="outlined" sx={cameraCardSx}>
                          <Box
                            sx={{
                              position: 'relative',
                              height: 150,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 3,
                              overflow: 'hidden',
                              background: '#f0f2f5'
                            }}
                            onClick={() => handleOpenDialog(image, selectedBay || bays[0])}
                            style={{ cursor: 'pointer' }}
                          >
                            <img
                              src={image.image_url}
                              alt={`Camera ${index + 1} - ${(selectedBay || bays[0]).bay_name}`}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 0 }}
                            />
                            <Box>{/* {`Camera ${index + 1}`} */}</Box>
                          </Box>
                          <CardContent sx={{ p: 1, pb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {(() => {
                                const ts = image.metadata[0]?.timestamp;
                                if (!ts) return '';
                                const dateObj = new Date(ts);
                                if (isNaN(dateObj.getTime())) return '';
                                return `Captured at ${dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                              })()}
                            </Typography>
                          </CardContent>
                        </Card>
                      ))
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '220px',
                          backgroundColor: '#f5f5f5',
                          borderRadius: 2,
                          width: '100%',
                          border: '2px dashed #bdbdbd',
                          mt: 2
                        }}
                      >
                        <CameraAltIcon sx={{ fontSize: 56, color: '#bdbdbd', mb: 1 }} />
                        <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                          No camera images available for this bay
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          Try selecting a different bay or check back later.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      backgroundColor: '#f5f5f5',
                      borderRadius: 2
                    }}
                  >
                    <CameraAltIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                      No Bay Selected
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center">
                      Select a bay from the list to view its camera images
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* ImageDetailsDialog integration */}
      <ImageDetailsDialog
        dialogOpen={dialogOpen}
        handleCloseDialog={handleCloseDialog}
        selectedImage={selectedImage}
        imageLoaded={imageLoaded}
        setImageLoaded={setImageLoaded}
        formatTimestamp={formatTimestamp}
        bayDetails={bayDetails}
      />
    </Box>
  );
};

export default CameraView;
